import { Link } from "react-router-dom";
import {
  adminExport,
  adminExportCollection,
  adminImport,
  adminSeed,
  adminGetNotification,
  adminSetNotification,
} from "../../services/adminDataService";
import { useState, useEffect } from "react";
import { reloadFestivalData } from "../../services/dataService";

const collections = [
  { key: "artists", label: "Artists" },
  { key: "days", label: "Days" },
  { key: "stages", label: "Stages" },
  { key: "performances", label: "Performances" },
  { key: "festivalInfo", label: "Festival Info" },
];

export const AdminDashboard = () => {
  const [importing, setImporting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [exportingCollection, setExportingCollection] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [notificationTitle, setNotificationTitle] = useState("Értesítés");
  const [newNotification, setNewNotification] = useState("");
  const [validityHours, setValidityHours] = useState(24);
  const [validityMinutes, setValidityMinutes] = useState(0);
  const [savingNotification, setSavingNotification] = useState(false);

  useEffect(() => {
    const fetchNotification = async () => {
      try {
        const notif = await adminGetNotification();
        setNotificationTitle(
          notif.title !== undefined && notif.title !== "" ? notif.title : "Értesítés",
        );
        setNewNotification(notif.message || "");
        setValidityHours(notif.validityHours !== undefined ? notif.validityHours : 24);
        setValidityMinutes(notif.validityMinutes !== undefined ? notif.validityMinutes : 0);
      } catch (err) {
        console.error("Failed to load notification:", err);
      }
    };
    fetchNotification();
  }, []);

  const handleSaveNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingNotification(true);
    try {
      await adminSetNotification(
        notificationTitle,
        newNotification,
        validityHours,
        validityMinutes,
      );
      alert("Notification saved successfully!");
    } catch (err: unknown) {
      alert(`Failed to save notification: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSavingNotification(false);
    }
  };

  const handleExport = async () => {
    try {
      await adminExport();
    } catch (err: unknown) {
      alert(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setError(null);
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      if (!window.confirm("Are you sure you want to overwrite data with this import?")) {
        setImporting(false);
        return;
      }
      await adminImport(bundle);
      await reloadFestivalData(true);
      alert("Import successful!");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const handleSeed = async () => {
    if (
      !window.confirm(
        "Are you sure you want to seed the database? This will overwrite existing data with local JSON files!",
      )
    ) {
      return;
    }
    setSeeding(true);
    setError(null);
    try {
      const res = await adminSeed();
      await reloadFestivalData(true);
      alert(res.message || "Database seeded successfully!");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSeeding(false);
    }
  };

  const handleExportCollection = async (collection: string) => {
    setExportingCollection(collection);
    try {
      await adminExportCollection(collection as any);
    } catch (err: unknown) {
      alert(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setExportingCollection(null);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold font-['Unbounded'] text-white">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {collections.map((c) => (
          <div
            key={c.key}
            className="flex flex-col bg-card border border-border rounded-xl shadow hover:shadow-lg hover:border-main/20 transition-all duration-300"
          >
            <Link
              to={`/admin/${c.key}`}
              className="flex-1 p-6 block hover:bg-card-hover transition-colors rounded-t-xl"
            >
              <h2 className="text-xl font-semibold capitalize text-main font-['Unbounded']">
                {c.label}
              </h2>
              <p className="text-muted text-sm mt-2">Manage {c.label}</p>
            </Link>
            <div className="px-4 pb-4">
              <button
                type="button"
                onClick={() => handleExportCollection(c.key)}
                disabled={exportingCollection === c.key}
                className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-text-muted hover:text-main border border-border hover:border-main/40 bg-background-accent hover:bg-card px-3 py-1.5 rounded-lg transition-all cursor-pointer disabled:opacity-50"
              >
                <svg
                  className="h-3.5 w-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  role="img"
                  aria-label="Download"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                {exportingCollection === c.key ? "Exporting..." : `Export ${c.label}`}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-card border border-border p-6 rounded-xl mt-8 shadow-md">
        <h2 className="text-xl font-bold mb-4 font-['Unbounded'] text-white">Database Actions</h2>
        <div className="flex flex-wrap gap-4 items-center">
          <button
            type="button"
            onClick={handleExport}
            className="bg-accent text-background hover:bg-accent-hover px-4 py-2.5 rounded-lg font-semibold transition-all cursor-pointer"
          >
            Export JSON Bundle
          </button>

          <div className="relative">
            <input
              type="file"
              accept=".json"
              onChange={handleImport}
              disabled={importing}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <button
              type="button"
              className="bg-main text-background px-4 py-2.5 rounded-lg font-semibold transition-all pointer-events-none"
            >
              {importing ? "Importing..." : "Import JSON Bundle"}
            </button>
          </div>

          <button
            type="button"
            onClick={handleSeed}
            disabled={seeding}
            className="bg-[#d97ab8] text-background hover:bg-[#c869a7] disabled:opacity-50 px-4 py-2.5 rounded-lg font-semibold transition-all cursor-pointer"
          >
            {seeding ? "Seeding..." : "Reset / Seed Database"}
          </button>
        </div>

        {error && (
          <div className="mt-4 text-red-400 text-sm font-mono p-3 bg-red-950/30 border border-red-900/50 rounded-lg">
            {error}
          </div>
        )}
      </div>

      {/* ── Manage Notification ── */}
      <div className="bg-card border border-border p-6 rounded-xl mt-8 shadow-md">
        <h2 className="text-xl font-bold mb-4 font-['Unbounded'] text-white">
          System Notification
        </h2>
        <form onSubmit={handleSaveNotification} className="space-y-4">
          <div>
            <label
              htmlFor="title-input"
              className="block text-sm font-semibold text-text-muted mb-2"
            >
              Notification Title:
            </label>
            <input
              id="title-input"
              type="text"
              value={notificationTitle}
              onChange={(e) => setNotificationTitle(e.target.value)}
              placeholder="Értesítés"
              className="w-full bg-background-accent border border-border rounded-lg p-3 text-sm text-white focus:outline-none focus:border-main/50 transition-all"
            />
          </div>
          <div>
            <label
              htmlFor="notification-input"
              className="block text-sm font-semibold text-text-muted mb-2"
            >
              Current Notification Message:
            </label>
            <textarea
              id="notification-input"
              value={newNotification}
              onChange={(e) => setNewNotification(e.target.value)}
              placeholder="Enter system notification message..."
              rows={3}
              className="w-full bg-background-accent border border-border rounded-lg p-3 text-sm text-white focus:outline-none focus:border-main/50 transition-all resize-none"
            />
          </div>
          <div className="flex gap-4">
            <div>
              <label
                htmlFor="validity-hours-input"
                className="block text-sm font-semibold text-text-muted mb-2"
              >
                Validity (Hours):
              </label>
              <input
                id="validity-hours-input"
                type="number"
                min={0}
                value={validityHours}
                onChange={(e) => setValidityHours(Number(e.target.value))}
                className="w-24 bg-background-accent border border-border rounded-lg p-3 text-sm text-white focus:outline-none focus:border-main/50 transition-all"
              />
            </div>
            <div>
              <label
                htmlFor="validity-minutes-input"
                className="block text-sm font-semibold text-text-muted mb-2"
              >
                Validity (Minutes):
              </label>
              <input
                id="validity-minutes-input"
                type="number"
                min={0}
                max={59}
                value={validityMinutes}
                onChange={(e) => setValidityMinutes(Number(e.target.value))}
                className="w-24 bg-background-accent border border-border rounded-lg p-3 text-sm text-white focus:outline-none focus:border-main/50 transition-all"
              />
            </div>
          </div>
          <div className="flex justify-between items-center flex-wrap gap-2">
            <p className="text-xs text-text-muted">
              This message will be shown to users in the app when they check for updates.
            </p>
            <button
              type="submit"
              disabled={savingNotification}
              className="bg-main text-background hover:bg-main/80 disabled:opacity-50 px-4 py-2 rounded-lg font-semibold transition-all cursor-pointer text-sm"
            >
              {savingNotification ? "Saving..." : "Save Notification"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminDashboard;
